import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Flame, Hand, HelpCircle, Lightbulb, LogOut, Send, Sparkles, Timer, Turtle, Users, Zap,
} from "lucide-react";
import { toast } from "sonner";
import { getStudentId } from "@/lib/api";
import {
  askAiInSession, endStudySession, getStudyState, leaveStudySession, requestHint, respondToQuestion, sendPeerMessage,
  sendReaction, clearActiveSessionCode,
} from "@/lib/studyApi";
import { useStudySocket } from "@/hooks/useStudySocket";

const REACTIONS = [
  { key: "raise_hand", label: "Raise Hand", icon: Hand },
  { key: "confused", label: "I'm Confused", icon: HelpCircle },
  { key: "understand", label: "I Understand", icon: Lightbulb },
  { key: "challenge", label: "Give Me a Challenge", icon: Flame },
  { key: "slow_down", label: "Slow Down", icon: Turtle },
];

function fmt(seconds) {
  const m = Math.floor(seconds / 60).toString().padStart(2, "0");
  const s = Math.floor(seconds % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

export default function StudyLive() {
  const { code } = useParams();
  const navigate = useNavigate();
  const studentId = getStudentId();
  const [state, setState] = useState(null);
  const [feed, setFeed] = useState([]);
  const [answerText, setAnswerText] = useState("");
  const [answered, setAnswered] = useState(false);
  const [submittedBy, setSubmittedBy] = useState(new Set());
  const [elapsed, setElapsed] = useState(0);
  const [chatText, setChatText] = useState("");
  const [askOpen, setAskOpen] = useState(false);
  const [askText, setAskText] = useState("");
  const feedEndRef = useRef(null);

  const pushFeed = (item) => setFeed((prev) => [...prev, { ...item, id: `${Date.now()}-${Math.random()}` }]);

  const load = () => {
    getStudyState(code, studentId).then(({ data }) => {
      setState(data);
      setAnswered(data.i_have_answered);
    }).catch(() => navigate("/"));
  };

  useEffect(() => { load(); }, [code]);

  useEffect(() => {
    if (!state?.session?.started_at) return;
    const start = new Date(state.session.started_at).getTime();
    const tick = () => setElapsed(Math.floor((Date.now() - start) / 1000));
    tick();
    const int = setInterval(tick, 1000);
    return () => clearInterval(int);
  }, [state?.session?.started_at]);

  useEffect(() => { feedEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [feed]);

  useStudySocket(code, studentId, (msg) => {
    if (msg.type === "LESSON_STARTED") {
      pushFeed({ type: "teacher", text: msg.message });
    } else if (msg.type === "QUESTION_STARTED") {
      setAnswered(false);
      setAnswerText("");
      setSubmittedBy(new Set());
      setState((prev) => prev && { ...prev, current_question: msg, last_revealed: null });
      pushFeed({ type: "question", text: msg.question_text, questionIndex: msg.question_index });
    } else if (msg.type === "ANSWER_SUBMITTED") {
      setSubmittedBy((prev) => new Set(prev).add(msg.student_id));
    } else if (msg.type === "ANSWER_EVALUATED") {
      pushFeed({ type: "reveal", explanation: msg.explanation, results: msg.results });
      load();
    } else if (msg.type === "ANSWER_ACK") {
      toast[msg.correct ? "success" : "message"](msg.feedback);
    } else if (msg.type === "HINT") {
      pushFeed({ type: "hint", text: msg.hint });
    } else if (msg.type === "BONUS_CHALLENGE") {
      pushFeed({ type: "bonus", text: msg.message });
    } else if (msg.type === "REACTION_CHANGED") {
      pushFeed({ type: "reaction", studentId: msg.student_id, name: msg.display_name, reaction: msg.reaction });
    } else if (msg.type === "TEACHER_NOTE") {
      pushFeed({ type: "teacher", text: msg.message });
    } else if (msg.type === "PEER_MESSAGE") {
      pushFeed({ type: "peer", studentId: msg.student_id, name: msg.display_name, text: msg.text });
    } else if (msg.type === "TEACHER_MESSAGE") {
      pushFeed({ type: "qa", name: msg.display_name, question: msg.question, answer: msg.answer });
    } else if (msg.type === "MODERATION_NOTICE") {
      toast.message(msg.message);
    } else if (msg.type === "STUDENT_JOINED" || msg.type === "STUDENT_LEFT" || msg.type === "STUDENT_DISCONNECTED" || msg.type === "HOST_TRANSFERRED") {
      load();
    } else if (msg.type === "SESSION_COMPLETED") {
      navigate(`/study/complete/${code}`);
    }
  });

  const submitAnswer = async () => {
    if (!answerText.trim() || !state?.current_question) return;
    try {
      await respondToQuestion(code, studentId, state.current_question.question_id, answerText.trim());
      setAnswered(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't submit your answer.");
    }
  };

  const askHint = async () => {
    if (!state?.current_question) return;
    try { await requestHint(code, studentId, state.current_question.question_id); } catch { /* noop */ }
  };

  const react = async (reaction) => {
    try { await sendReaction(code, studentId, reaction); } catch { /* noop */ }
  };

  const sendChat = async () => {
    if (!chatText.trim()) return;
    const text = chatText.trim();
    setChatText("");
    try { await sendPeerMessage(code, studentId, text); } catch { /* noop */ }
  };

  const sendAsk = async () => {
    if (!askText.trim()) return;
    const text = askText.trim();
    setAskText("");
    setAskOpen(false);
    try { await askAiInSession(code, studentId, text); } catch { /* noop */ }
  };

  const leave = async () => {
    await leaveStudySession(code, studentId).catch(() => {});
    clearActiveSessionCode();
    navigate("/");
  };

  const endSession = async () => {
    if (!window.confirm("End this session for everyone?")) return;
    try { await endStudySession(code, studentId); } catch { toast.error("Couldn't end the session."); }
  };

  if (!state) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading...</div>;
  const isHost = state.my_role === "host";

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="study-live-page">
      <div className="border-b border-border px-4 sm:px-6 h-16 flex items-center justify-between shrink-0 bg-card">
        <div>
          <p className="font-heading font-bold text-sm sm:text-base">{state.session.topic}</p>
          <p className="text-xs text-muted-foreground">{state.session.subject} · Class {state.session.standard}</p>
        </div>
        <div className="flex items-center gap-2 sm:gap-4">
          <div className="flex items-center gap-1 text-xs sm:text-sm font-bold text-muted-foreground" data-testid="study-session-timer">
            <Timer size={14} /> {fmt(elapsed)}
          </div>
          {isHost && (
            <button onClick={endSession} data-testid="study-end-session-button" className="text-xs font-bold text-destructive hover:opacity-80">End</button>
          )}
          <button onClick={leave} data-testid="study-leave-button" className="text-muted-foreground hover:text-foreground">
            <LogOut size={17} />
          </button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden flex-col lg:flex-row">
        <div className="lg:w-64 border-b lg:border-b-0 lg:border-r border-border p-4 bg-card shrink-0" data-testid="study-live-progress-panel">
          <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5"><Users size={13} /> Live Session Progress</p>
          <div className="space-y-2">
            {state.participants.map((p) => (
              <div key={p.student_id} className="flex items-center justify-between bg-muted rounded-xl px-3 py-2" data-testid={`progress-participant-${p.student_id}`}>
                <span className="text-sm font-semibold truncate">{p.display_name}{submittedBy.has(p.student_id) && <span className="text-success ml-1">✓</span>}</span>
                <span className="text-xs font-bold text-secondary flex items-center gap-0.5"><Sparkles size={11} /> {p.smart_points}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4" data-testid="study-live-feed">
            {feed.map((item) => (
              <FeedItem key={item.id} item={item} myStudentId={studentId} />
            ))}
            <div ref={feedEndRef} />
          </div>

          {state.current_question && (
            <div className="border-t border-border p-4 bg-card" data-testid="study-live-answer-panel">
              {!answered ? (
                <div className="flex gap-2">
                  <input
                    data-testid="study-answer-input"
                    value={answerText}
                    onChange={(e) => setAnswerText(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && submitAnswer()}
                    placeholder="Think & Answer..."
                    className="flex-1 h-11 rounded-xl border border-border bg-background px-4 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                  <button onClick={askHint} data-testid="study-hint-button" className="h-11 px-3 rounded-xl border border-border text-xs font-bold hover:bg-muted">Hint</button>
                  <button onClick={submitAnswer} data-testid="study-submit-answer-button" className="h-11 px-5 rounded-xl bg-primary text-primary-foreground font-bold text-sm hover:bg-primary/90 active:scale-95">Submit</button>
                </div>
              ) : (
                <p className="text-sm text-center text-muted-foreground" data-testid="study-waiting-others">Waiting for others to answer... ({submittedBy.size}/{state.participants.length})</p>
              )}
            </div>
          )}

          <div className="border-t border-border p-3 bg-card space-y-2">
            <div className="flex flex-wrap gap-1.5" data-testid="study-reactions-bar">
              {REACTIONS.map((r) => (
                <button key={r.key} onClick={() => react(r.key)} data-testid={`reaction-${r.key}`} title={r.label}
                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-full border border-border text-xs font-semibold hover:border-primary/50">
                  <r.icon size={13} /> <span className="hidden sm:inline">{r.label}</span>
                </button>
              ))}
              <button onClick={() => setAskOpen((v) => !v)} data-testid="reaction-ask_question" title="Ask a Question"
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-full border border-border text-xs font-semibold hover:border-primary/50">
                <Zap size={13} /> <span className="hidden sm:inline">Ask a Question</span>
              </button>
            </div>
            {askOpen && (
              <div className="flex gap-2">
                <input data-testid="ask-ai-input" value={askText} onChange={(e) => setAskText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendAsk()}
                  placeholder="Ask SmartGuru..." className="flex-1 h-10 rounded-lg border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40" />
                <button onClick={sendAsk} data-testid="ask-ai-send-button" className="h-10 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-bold">Ask</button>
              </div>
            )}
            <div className="flex gap-2">
              <input data-testid="peer-chat-input" value={chatText} onChange={(e) => setChatText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendChat()}
                placeholder="Message the group..." className="flex-1 h-10 rounded-lg border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40" />
              <button onClick={sendChat} data-testid="peer-chat-send-button" className="h-10 px-3 rounded-lg border border-border hover:bg-muted"><Send size={15} /></button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FeedItem({ item, myStudentId }) {
  if (item.type === "teacher") {
    return <div className="bg-primary/10 border border-primary/20 rounded-2xl p-4 text-sm" data-testid="feed-teacher-message"><span className="font-bold text-primary">SmartGuru: </span>{item.text}</div>;
  }
  if (item.type === "question") {
    return (
      <div className="bg-secondary/15 border border-secondary/30 rounded-2xl p-4" data-testid="feed-question">
        <p className="text-[10px] font-bold uppercase tracking-widest text-secondary-foreground/70 mb-1">Think & Answer — Question {item.questionIndex + 1}</p>
        <p className="text-sm font-semibold">{item.text}</p>
      </div>
    );
  }
  if (item.type === "reveal") {
    return (
      <div className="bg-muted rounded-2xl p-4 space-y-2" data-testid="feed-reveal">
        <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Results</p>
        {item.results.map((r) => (
          <p key={r.student_id} className="text-sm flex justify-between">
            <span className="font-semibold">{r.display_name}: {r.response ?? "no answer"}</span>
            <span>{r.correct === true ? "✓" : r.correct === false ? "✕" : "—"}</span>
          </p>
        ))}
        {item.explanation && <p className="text-xs text-muted-foreground pt-1 border-t border-border">{item.explanation}</p>}
      </div>
    );
  }
  if (item.type === "hint") {
    return <div className="bg-accent/10 border border-accent/20 rounded-2xl p-3 text-sm" data-testid="feed-hint"><span className="font-bold text-accent">Hint (just for you): </span>{item.text}</div>;
  }
  if (item.type === "bonus") {
    return <div className="bg-success/10 border border-success/20 rounded-2xl p-3 text-sm" data-testid="feed-bonus"><span className="font-bold text-success">SmartGuru: </span>{item.text}</div>;
  }
  if (item.type === "reaction") {
    return <p className="text-xs text-center text-muted-foreground" data-testid="feed-reaction">{item.name} reacted: {item.reaction.replaceAll("_", " ")}</p>;
  }
  if (item.type === "peer") {
    const isMe = item.studentId === myStudentId;
    return (
      <div className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm ${isMe ? "ml-auto bg-secondary text-secondary-foreground" : "bg-muted"}`} data-testid="feed-peer-message">
        <span className="font-bold">{item.name}: </span>{item.text}
      </div>
    );
  }
  if (item.type === "qa") {
    return (
      <div className="space-y-1" data-testid="feed-qa">
        <p className="text-sm bg-muted rounded-xl px-3 py-2 max-w-[80%]"><span className="font-bold">{item.name} asked: </span>{item.question}</p>
        <p className="text-sm bg-primary/10 border border-primary/20 rounded-xl px-3 py-2 max-w-[80%]"><span className="font-bold text-primary">SmartGuru: </span>{item.answer}</p>
      </div>
    );
  }
  return null;
}
